import { parseNodeRouteUrl, type NodeRoute } from './nodeRoute.js';
import { resolveProjectSurfaceDescriptor } from './projectSurfaceDescriptor.js';
import type { ProjectSurfaceDescriptor } from './projectShellRouter.js';

export interface InitialProjectLoad {
  readonly href: string;
  readonly route: NodeRoute;
  readonly descriptor: Promise<ProjectSurfaceDescriptor>;
  readonly rendererReady: Promise<void>;
  abort(): void;
}

export interface ProtectedProjectModule {
  startProtectedProject(options: {
    initialUrl: URL;
    initialLoad: InitialProjectLoad | null;
  }): void;
  disposeProtectedEntry?: () => void;
}

export interface ProtectedProjectLoadOptions {
  location: Pick<Location, 'href'>;
  apiBase: string;
  basePath: string;
  prepareTransport?: () => Promise<void>;
  importProduct(): Promise<ProtectedProjectModule>;
  resolveDescriptor?: typeof resolveProjectSurfaceDescriptor;
  preloadBoardRenderer?: () => Promise<void>;
}

let rendererModuleReady: Promise<void> | null = null;

function preloadDefaultBoardRenderer(): Promise<void> {
  rendererModuleReady ??= import('../canvas/webglRendererPreload.js').then((renderer) => {
    void renderer.WebGLRenderer;
  });
  return rendererModuleReady;
}

function canonicalHref(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

export function createInitialProjectLoad(
  initialUrl: URL,
  options: Pick<
    ProtectedProjectLoadOptions,
    'apiBase' | 'basePath' | 'resolveDescriptor' | 'preloadBoardRenderer'
  >,
): InitialProjectLoad | null {
  if (initialUrl.searchParams.get('demo') === '1') return null;
  const route = parseNodeRouteUrl(initialUrl, options.basePath);
  if (route === null) return null;

  const controller = new AbortController();
  const descriptor = (options.resolveDescriptor ?? resolveProjectSurfaceDescriptor)(
    route,
    controller.signal,
    options.apiBase,
  );
  // The product chunk may still be loading when the request fails. Keep the
  // original rejecting promise for the router while marking it handled now.
  void descriptor.catch(() => undefined);

  const rendererReady = route.kind === 'board'
    ? (options.preloadBoardRenderer ?? preloadDefaultBoardRenderer)().catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.debug('GoCanvas: WebGL renderer preload failed; normal initialization will retry', error);
    })
    : Promise.resolve();

  return {
    href: canonicalHref(initialUrl),
    route,
    descriptor,
    rendererReady,
    abort: () => controller.abort(),
  };
}

export async function loadProtectedProject(
  options: ProtectedProjectLoadOptions,
): Promise<ProtectedProjectModule> {
  const initialUrl = new URL(options.location.href);
  const transportReady = options.prepareTransport?.();
  const productPromise = options.importProduct();
  // Transport setup and the product import start together. Keep import
  // rejection handled while a development transport such as MSW initializes.
  void productPromise.catch(() => undefined);
  let initialLoad: InitialProjectLoad | null = null;
  try {
    if (transportReady !== undefined) await transportReady;
    initialLoad = createInitialProjectLoad(initialUrl, options);
    const product = await productPromise;
    product.startProtectedProject({ initialUrl, initialLoad });
    return product;
  } catch (error) {
    initialLoad?.abort();
    throw error;
  }
}
